import type { GaVueComponent } from "@/common/vueUtils";
import type { ContentItemType, ShareContent, ShareMedium, ShareProps } from "@newgenerated/shared/schema";
import { Language } from "@newgenerated/shared/schema";
import { useCustomModal } from "@/components/general/GaModal";
import { GaButton } from "@/components/general/GaButton";
import { GaFormFieldInputText } from "@/components/form/GaFormFieldInputText";
import { addToast } from "@/components/general/GaToast";
import type { TranslationFn } from "@utils/vue-migration/translationUtils";
import { createStore, type Store } from "@/common/storeUtils";
import { GaFormFieldTextArea } from "@/components/form/GaFormFieldTextArea";
import { useGaContext } from "@utils/vue-migration/common/gaContext/gaContext";
import "./_sharer.scss";
import { trackShareEvent } from "@newgenerated/shared/stubs/trackShareEvent";
import { getSummaryShareQrCode } from "@newgenerated/shared/stubs/getSummaryShareQrCode";
import type { DeepReadonly, SetupContext } from "vue";
import { enrichRelativeUrl } from "@utils/vue-migration/common/urlUtils";
import type { AspContext } from "@utils/widgetContextUtils";

export type SharerState = { view: "DEFAULT" | "IFRAME" | "QRCODE"; qrCodeBase64: string | null };

const openShareWindow = (event: Event, url: string, width: number, height: number, fn?: (dialog: Window) => void): boolean => {
  event.preventDefault();
  const dlg = window.open(url, "getAbstractSharer", `height=${height},width=${width}`);
  if (dlg !== null && fn !== undefined) {
    fn(dlg);
  }
  return false;
};

const FaceBook = (props: { contentId: bigint | string; type: ContentItemType; share: () => void }): GaVueComponent => {
  const { type, contentId } = props;
  const url = `/share/facebook?t=${type}&r=${contentId}`;

  return (
    <a
      href={url}
      title="Facebook"
      target="_blank"
      rel="nofollow"
      onClick={(event) => {
        openShareWindow(event, url, 500, 410);
        props.share();
      }}>
      <img src="/www/images/share/facebook.svg" alt="facebook" />
    </a>
  );
};

const FaceBookWorkplace = (props: { link: URL; share: () => void }): GaVueComponent => {
  const { link } = props;
  const url = `https://work.facebook.com/sharer.php?display=popup&u=${link.toString()}`;

  return (
    <a
      href={url}
      title="Facebook"
      target="_blank"
      rel="nofollow"
      onClick={(event) => {
        openShareWindow(event, url, 500, 410);
        props.share();
      }}>
      <img src="/www/images/share/fb_workplace.svg" alt="facebook workplace" />
    </a>
  );
};

const Twitter = (props: { contentId: bigint | string; type: ContentItemType; share: () => void }): GaVueComponent => {
  const { type, contentId } = props;
  const url = `/share/twitter?t=${type}&r=${contentId}`;
  return (
    <a
      href={url}
      title="Twitter"
      target="_blank"
      rel="nofollow"
      onClick={(event) => {
        openShareWindow(event, url, 500, 227);
        props.share();
      }}>
      <img src="/www/images/share/twitter.svg" alt="twitter" />
    </a>
  );
};

const WeChat = (props: { contentId: bigint | string; type: ContentItemType; share: () => void }): GaVueComponent => {
  const { type, contentId } = props;
  const url = `/share/wechat?t=${type}&r=${contentId}`;
  return (
    <a
      href={url}
      title="WeChat"
      target="_blank"
      rel="nofollow"
      onClick={(event) => {
        openShareWindow(event, url, 500, 450);
        props.share();
      }}>
      <img src="/www/images/share/wechat.svg" alt="&#x5FAE;&#x4FE1;" />
    </a>
  );
};

const Xing = (props: { contentId: bigint | string; type: ContentItemType; share: () => void }): GaVueComponent => {
  const { type, contentId } = props;
  const url = `/share/xing?t=${type}&r=${contentId}`;
  return (
    <a
      href={url}
      title="XING"
      target="_blank"
      rel="nofollow"
      onClick={(event) => {
        openShareWindow(event, url, 500, 450);
        props.share();
      }}>
      <img src="/www/images/share/xing.svg" alt="Xing" />
    </a>
  );
};

const LinkedIn = (props: { contentId: bigint | string; type: ContentItemType; share: () => void }): GaVueComponent => {
  const { type, contentId } = props;
  const url = `/share/linkedin?t=${type}&r=${contentId}`;
  return (
    <a
      href={url}
      title="LinkedIn"
      target="_blank"
      rel="nofollow"
      onClick={(event) => {
        openShareWindow(event, url, 500, 521);
        props.share();
      }}>
      <img src="/www/images/share/linkedin.svg" alt="LinkedIn" />
    </a>
  );
};

const Teams = (props: { contentId: bigint | string; type: ContentItemType; link: URL; share: () => void }): GaVueComponent => {
  const url = `https://teams.microsoft.com/share?href=${props.link.toString()}`;
  return (
    <a
      href={url}
      title="Teams"
      target="_blank"
      rel="nofollow"
      onClick={(event) => {
        openShareWindow(event, url, 500, 521);
        props.share();
      }}>
      <img src="/www/images/share/teams.svg" alt="Teams" />
    </a>
  );
};

const Scorm = (props: { contentId: bigint | string; type: ContentItemType; share: () => void }): GaVueComponent | null => {
  const { type, contentId } = props;
  const url = ((): string | null => {
    switch (type) {
      case "CHANNEL":
        return `/scorm/package/channel/${contentId}`;
      case "SUMMARY":
        return `/scorm/package/summary/${contentId}`;
      default:
        return null;
    }
  })();
  if (url == null) {
    return null;
  }

  return (
    <a
      href={url}
      title="Scorm"
      target="_blank"
      rel="nofollow"
      onClick={() => {
        props.share();
      }}>
      <img src="/www/images/share/scorm.svg" alt="scormText" />
    </a>
  );
};

const Email = (props: { contentId: bigint | string; type: ContentItemType; share: () => void }): GaVueComponent => {
  const { type, contentId } = props;
  const url = `/share/email/?t=${type.toLowerCase()}&r=${contentId}`;
  const dlgFn = (dlg: Window): void => {
    dlg.focus();
    setTimeout(() => {
      if (!dlg.document.hasFocus()) {
        dlg.close();
      }
    }, 500);
  };

  return (
    <a
      href={url}
      title="E-mail"
      target="_blank"
      rel="nofollow"
      onClick={(event) => {
        openShareWindow(event, url, 1, 1, dlgFn);
        props.share();
      }}>
      <img src="/www/images/share/mail.svg" alt="E-mail" />
    </a>
  );
};

const QrCode = (props: { onClick: () => void }): GaVueComponent => {
  return (
    <a
      title="Qr Code"
      onClick={(event) => {
        event.preventDefault();
        props.onClick();
      }}>
      <img src="/www/images/share/qr.svg" alt="QR Code" />
    </a>
  );
};

const copyToClipboard = async (link: string, t: TranslationFn): Promise<void> => {
  await navigator.clipboard.writeText(link);
  addToast({
    message: t("general:page.shareModal.copySuccessful", "copied to clipboard"),
  });
};

export function getDeviceType(): string {
  const viewportWidth = window.innerWidth;
  const DEVICE_MD = 792;
  const DEVICE_LG = 1260;
  if (viewportWidth < DEVICE_MD) {
    return "mobile";
  } else if (viewportWidth > DEVICE_MD && viewportWidth < DEVICE_LG) {
    return "tablet";
  } else {
    return "desktop";
  }
}

const CopyLink = (props: { t: TranslationFn; link: URL; additionalSearchParams?: URLSearchParams; share: () => void }): GaVueComponent => {
  const { additionalSearchParams, t, link, share } = props;
  if (additionalSearchParams !== undefined) {
    additionalSearchParams.forEach((value, key) => {
      link.searchParams.set(key, value);
    });
  }
  link.searchParams.set("utm_term", "copy-link");

  const url = link.toString();

  return (
    <div class="sharer-content__copy">
      <p class="caption">{t("general:page.shareModal.linkCaption")}</p>
      <div class="sharer-content__copy-input">
        <div class="sharer-content__copy-field">
          <GaFormFieldInputText name="miabShareLink" value={url} onUpdateValue={() => void 0} disabled={true} />
        </div>
        <div class="sharer-content__copy-action">
          <GaButton
            onClick={() => {
              void copyToClipboard(url, t);
              share();
            }}>
            {t("general:page.shareModal.copyLink")}
          </GaButton>
        </div>
      </div>
    </div>
  );
};

const ChannelIFrame = (props: { share: () => void }): GaVueComponent => {
  return (
    <a
      href="#"
      title="iframe"
      target="_blank"
      rel="nofollow"
      onClick={(e) => {
        e.preventDefault();
        props.share();
        return false;
      }}>
      <img src="/www/images/share/embed.svg" alt="embed" />
    </a>
  );
};

const IframeContentChannel = (props: { iframeUrl: URL; t: TranslationFn; lang: Language }): GaVueComponent => {
  const iframeContent = `<iframe width="100%" height="400" name="iframe" name="iframe-share" src="${props.iframeUrl}"></iframe>`;
  return (
    <div class="sharer-content__copy">
      <p class="caption">IFrame</p>
      <GaFormFieldTextArea value={iframeContent} onUpdateValue={() => void 0} name={"iframe"} />
      <div class="mt-3 text-end">
        <GaButton onClick={() => copyToClipboard(iframeContent, props.t)}>{props.t("general:button.copy")}</GaButton>
      </div>
    </div>
  );
};

const fetchQrCode = async (contentItemId: string | bigint, store: Store<SharerState>): Promise<void> => {
  const result = await getSummaryShareQrCode({ body: { dataId: BigInt(contentItemId) } });
  if (result.kind === "success") {
    store.sub("qrCodeBase64").set(result.data.base64);
  }
};

const QrCodeContent = (props: { base64: string | null; t: TranslationFn }): GaVueComponent => {
  return (
    <div class="sharer-content__qr text-center">
      <p class="caption" vHtml={props.t("summary:qrCode.description", "Scan this code to open the summary on your phone.")}></p>
      {props.base64 !== null ? <img class="sharer-content__qr-img" src={props.base64} alt="QR Code" width={150} height={150} /> : <p>{props.t("summary:qrCode.loading", "Generating QR code…")}</p>}
    </div>
  );
};

const ShareDescription = (props: { content: ShareContent }): GaVueComponent | null => {
  switch (props.content.shareType) {
    case "CONTENT":
      return null;
    case "CHANNEL": {
      return (
        <div class="sharer-content__description__channel">
          <div class="sharer-content__description__channel__cover">
            {props.content.channel.coverUri !== null ? <img class="sharer-content__description_channel__cover-img" src={props.content.channel.coverUri} alt="skill_cover" /> : null}
          </div>
          <div class="sharer-content__description__channel__title">
            <h4>{props.content.channel.title}</h4>
          </div>
        </div>
      );
    }
  }
};

const trackEvent = (contentId: string | bigint, type: ContentItemType, shareMedium: ShareMedium): void => {
  try {
    void trackShareEvent({
      body: {
        id: contentId.toString(),
        contentItemType: type,
        shareMedium: shareMedium,
      },
    });
  } catch (e) {
    console.error(e);
  }
};

function ShareIcon(props: { shareMedium: ShareMedium; contentItemId: string | bigint; contentItemType: ContentItemType; iframeLink?: URL; link: URL; store: Store<SharerState> }): GaVueComponent | null {
  const { shareMedium, store } = props;
  switch (shareMedium) {
    case "XING": {
      return (
        <Xing
          contentId={props.contentItemId}
          type={props.contentItemType}
          share={() => {
            trackEvent(props.contentItemId, props.contentItemType, "XING");
          }}
        />
      );
    }
    case "IFRAME": {
      return props.iframeLink !== undefined ? (
        <div>
          <ChannelIFrame
            share={() => {
              if (store.sub("view").get() === "DEFAULT") {
                store.sub("view").set("IFRAME");
              } else {
                store.sub("view").set("DEFAULT");
              }

              trackEvent(props.contentItemId, props.contentItemType, "IFRAME");
            }}
          />
        </div>
      ) : null;
    }
    case "COPY_LINK": {
      return null; //handled below
    }
    case "FACEBOOK": {
      return (
        <FaceBook
          contentId={props.contentItemId}
          type={props.contentItemType}
          share={() => {
            trackEvent(props.contentItemId, props.contentItemType, "FACEBOOK");
          }}
        />
      );
    }
    case "TWITTER": {
      return (
        <Twitter
          contentId={props.contentItemId}
          type={props.contentItemType}
          share={() => {
            trackEvent(props.contentItemId, props.contentItemType, "TWITTER");
          }}
        />
      );
    }
    case "LINKEDIN": {
      return (
        <LinkedIn
          contentId={props.contentItemId}
          type={props.contentItemType}
          share={() => {
            trackEvent(props.contentItemId, props.contentItemType, "LINKEDIN");
          }}
        />
      );
    }
    case "EMAIL": {
      return (
        <Email
          contentId={props.contentItemId}
          type={props.contentItemType}
          share={() => {
            trackEvent(props.contentItemId, props.contentItemType, "EMAIL");
          }}
        />
      );
    }
    case "SCORM": {
      return (
        <Scorm
          contentId={props.contentItemId}
          type={props.contentItemType}
          share={() => {
            trackEvent(props.contentItemId, props.contentItemType, "SCORM");
          }}
        />
      );
    }
    case "TEAMS": {
      return (
        <Teams
          contentId={props.contentItemId}
          type={props.contentItemType}
          link={props.link}
          share={() => {
            trackEvent(props.contentItemId, props.contentItemType, "TEAMS");
          }}
        />
      );
    }
    case "WECHAT": {
      return (
        <WeChat
          contentId={props.contentItemId}
          type={props.contentItemType}
          share={() => {
            trackEvent(props.contentItemId, props.contentItemType, "WECHAT");
          }}
        />
      );
    }
    case "FACEBOOK_WORKSPACE": {
      return (
        <FaceBookWorkplace
          link={props.link}
          share={() => {
            trackEvent(props.contentItemId, props.contentItemType, "FACEBOOK");
          }}
        />
      );
    }
    case "QRCODE": {
      return (
        <QrCode
          onClick={() => {
            const view = store.sub("view");
            if (view.get() === "QRCODE") {
              view.set("DEFAULT");
            } else {
              view.set("QRCODE");
              if (store.sub("qrCodeBase64").get() === null) {
                void fetchQrCode(props.contentItemId, store);
              }
            }
            trackEvent(props.contentItemId, props.contentItemType, "QRCODE");
          }}
        />
      );
    }
  }
}

export const getLocalizedContentItemName = (t: TranslationFn, contentItemType: ContentItemType): string => {
  switch (contentItemType) {
    case "UNKNOWN": {
      throw new Error('Not implemented yet: "UNKNOWN" case');
    }
    case "SUMMARY": {
      return t("general:link.summary", "general:link.summary");
    }
    case "CATEGORY": {
      throw new Error('Not implemented yet: "CATEGORY" case');
    }
    case "CHANNEL": {
      return t("general:link.channel", "general:link.channel");
    }
    case "ACTIONABLE": {
      return t("general:link.actionable", "general:link.actionable");
    }
    case "MYLIST": {
      return t("general:link.myList", "general:link.myList");
    }
    case "CUSTOMPAGE": {
      return t("general:link.customPage", "general:link.customPage");
    }
    case "EXPERT_VIDEO": {
      return t("general:link.expertVideos", "general:link.expertVideos");
    }
    case "MIAB": {
      return t("general:link.miab", "general:link.miab");
    }
    case "CUSTOMER_STORY": {
      return t("customerStories:customerStory.singular");
    }
    case "QUICKWIN": {
      return t("general:link.quickwin", "general:link.quickwin");
    }
    case "VERIFY": {
      return t("verify:link.verify", "Online Content");
    }
  }
};

export function SharerBody(props: {
  shareMedia: DeepReadonly<ShareMedium[]>;
  contentItemType: ContentItemType;
  contentItemId: string | bigint;
  link: URL;
  additionalSearchParams?: URLSearchParams;
  iframeLink?: URL;
  currentLanguage: Language;
  card?: GaVueComponent;
  t: TranslationFn;
  store?: Store<SharerState>;
  regionalAvailability?: string;
}): GaVueComponent {
  const store = props.store ?? createStore<SharerState>({ view: "DEFAULT", qrCodeBase64: null });
  return (
    <div class={"sharer-content"} dir={Language.getDir(props.currentLanguage)}>
      {props.card !== undefined && <div class={"sharer-content__description"}>{props.card}</div>}
      <div class={"sharer-content__body"}>
        <h3>{props.t("general:page.shareModal.title")}</h3>
        <p class="mb-4">{props.t("general:page.shareModal.description", [getLocalizedContentItemName(props.t, props.contentItemType)])}</p>
        {props.shareMedia.some((medium) => medium !== "COPY_LINK") && (
          <div class={"sharer-content__icons mb-5"}>
            {props.shareMedia.map((medium) => (
              <ShareIcon shareMedium={medium} contentItemId={props.contentItemId} contentItemType={props.contentItemType} link={props.link} store={store} iframeLink={props.iframeLink} />
            ))}
          </div>
        )}
        {store.sub("view").get() === "QRCODE" ? <QrCodeContent base64={store.sub("qrCodeBase64").get()} t={props.t} /> : null}
        {props.shareMedia.includes("COPY_LINK") ? (
          <div class={"sharer-content__copy d-flex flex-column gap-4"}>
            {store.sub("view").get() === "IFRAME" && props.iframeLink !== undefined ? <IframeContentChannel iframeUrl={props.iframeLink} t={props.t} lang={props.currentLanguage} /> : null}
            <CopyLink link={props.link} additionalSearchParams={props.additionalSearchParams} t={props.t} share={() => trackEvent(props.contentItemId, props.contentItemType, "COPY_LINK")} />
          </div>
        ) : null}
        {props.regionalAvailability !== undefined ? (
          <div class="alert alert-info mt-3">
            <p>{props.regionalAvailability}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export const openShareModal = async (props: {
  shareMedia: DeepReadonly<ShareMedium[]>;
  contentItemType: ContentItemType;
  contentItemId: string | bigint;
  link: URL;
  additionalSearchParams?: URLSearchParams;
  iframeLink?: URL;
  currentLanguage: Language;
  card: GaVueComponent;
  t?: TranslationFn;
  regionalAvailability?: string;
}): Promise<void> => {
  // Fallback for Prospect (SSG)
  const t = props.t ?? useGaContext().t;
  const store = createStore<SharerState>({ view: "DEFAULT", qrCodeBase64: null });

  await useCustomModal()({
    title: `Share`,
    body: () => <SharerBody {...props} t={t} store={store} />,
    size: "lg",
  });
};

export const ShareButton = (
  props: {
    shareMedia: ShareMedium[];
    contentItemType: ContentItemType;
    contentItemId: string | bigint;
    link: URL;
    additionalSearchParams?: URLSearchParams;
    utmCampaign?: string;
    iframeLink?: URL;
    currentLanguage: Language;
    t?: TranslationFn;
  },
  { slots }: SetupContext,
): GaVueComponent => {
  const getSearchParams = (): URLSearchParams | undefined => {
    if (props.additionalSearchParams === undefined && props.utmCampaign === undefined) {
      return undefined;
    }
    const { asp, customer } = useGaContext();
    const params = props.additionalSearchParams ?? new URLSearchParams();
    params.set("utm_source", `intUser-${asp !== null ? asp.corporateId : 0}-${customer?.customerId ?? 0}`);
    params.set("utm_medium", `${getDeviceType()}-web`);
    if (props.utmCampaign !== undefined) {
      params.set("utm_campaign", props.utmCampaign);
    }
    return params;
  };

  return <GaButton onClick={() => openShareModal({ ...props, additionalSearchParams: getSearchParams(), card: <>{slots.default?.()}</> })}>Share</GaButton>;
};

/*
 * not supported yet, Yammer, Jive, slack
 * */
export const Sharer = (props: { shareProps: ShareProps; ctx: { currentLanguage: Language; t: TranslationFn; asp: AspContext } }): GaVueComponent => {
  const contentItemType =
    props.shareProps.content.shareType === "CHANNEL"
      ? props.shareProps.content.shareType
      : props.shareProps.content.metadata.contentType === "ONLINE_CONTENT" || props.shareProps.content.metadata.contentType === "QUICKWIN"
        ? "UNKNOWN"
        : props.shareProps.content.metadata.contentType;

  const contentId = props.shareProps.content.shareType === "CHANNEL" ? props.shareProps.content.channel.channelId : props.shareProps.content.metadata.contentId.id;
  const { asp, customer } = useGaContext();

  const link = ((): URL => {
    const url = ((): URL => {
      switch (props.shareProps.content.shareType) {
        case "CONTENT":
          return new URL(props.shareProps.content.metadata.common.link ?? "", window.location.origin);
        case "CHANNEL":
          return new URL(`/channels/${props.shareProps.content.channel.name}/${props.shareProps.content.channel.channelId}`, window.location.origin);
      }
    })();
    if (asp !== null) {
      url.searchParams.set("u", asp.username);
    }

    return url;
  })();

  const additionalSearchParams = new URLSearchParams();
  additionalSearchParams.set("utm_source", `intUser-${asp !== null ? asp.corporateId : 0}-${customer?.customerId ?? 0}`);
  additionalSearchParams.set("utm_medium", `${getDeviceType()}-web`);
  additionalSearchParams.set("utm_campaign", `${contentItemType.toLowerCase()}-share`);

  const channel = props.shareProps.content.shareType === "CHANNEL" ? props.shareProps.content.channel : undefined;
  const iframeLink = channel !== undefined ? new URL(enrichRelativeUrl(`/skills/${channel.name}/${channel.channelId}/iframe`, props.ctx), window.location.origin) : undefined;

  return (
    <GaButton
      variant="primary-alt"
      onClick={() =>
        openShareModal({
          shareMedia: props.shareProps.shareOptions,
          currentLanguage: props.ctx.currentLanguage,
          t: props.ctx.t,
          contentItemType: contentItemType,
          contentItemId: contentId,
          link: link,
          iframeLink: iframeLink,
          additionalSearchParams: additionalSearchParams,
          card: <ShareDescription content={props.shareProps.content} />,
        })
      }>
      <i class="ico-share-alt2"></i>
      {props.ctx.t("general:share.short")}
    </GaButton>
  );
};
